import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminListWhatsAppLeads } from "../../services/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminWhatsAppLeads() {
  const { user, ready } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready && user?.role === "admin") {
      adminListWhatsAppLeads()
        .then(setLeads)
        .catch(() => toast.error("Could not load WhatsApp leads."))
        .finally(() => setLoading(false));
    }
  }, [ready, user]);

  const exportCsv = () => {
    if (!leads.length) return;
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["whatsapp", "name", "consent", "source", "created_at"];
    const rows = leads.map((l) => header.map((k) => esc(l[k])).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "whatsapp_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  return (
    <AdminLayout
      title="WhatsApp Leads"
      subtitle={`${leads.length} consented contact${leads.length === 1 ? "" : "s"}`}
      actions={
        <button
          onClick={exportCsv}
          disabled={!leads.length}
          data-testid="wa-leads-export"
          className="inline-flex items-center gap-2 bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      }
    >
      <div data-testid="admin-whatsapp-leads-page">
        {loading ? (
          <div className="border border-brand-sand bg-brand-surface p-10 text-center text-sm text-brand-stone" data-testid="wa-leads-loading">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="border border-brand-sand bg-brand-surface p-10 text-center text-sm text-brand-stone" data-testid="wa-leads-empty">No WhatsApp opt-ins yet. Consented sign-ups from the storefront will appear here.</div>
        ) : (
          <div className="overflow-x-auto border border-brand-sand bg-brand-surface">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-brand-sand text-[11px] uppercase tracking-[0.14em] text-brand-stone">
                  <th className="px-4 py-3">WhatsApp Number</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Consent</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Joined</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.lead_id} className="border-b border-brand-sand/60" data-testid={`wa-lead-${l.lead_id}`}>
                    <td className="px-4 py-3 font-medium text-brand-charcoal">{l.whatsapp}</td>
                    <td className="px-4 py-3 text-brand-stone">{l.name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${l.consent ? "bg-brand-orange/10 text-brand-orange" : "bg-brand-sand/50 text-brand-stone"}`}>
                        {l.consent ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-brand-stone">{l.source || "—"}</td>
                    <td className="px-4 py-3 text-brand-stone">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
